import React, { useRef, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import Slider from 'react-slick';
import classNames from 'classnames';
import ArrowLeftIcon from 'mdi-react/ChevronLeftIcon';
import ArrowRightIcon from 'mdi-react/ChevronRightIcon';
// @ts-ignore
import InnerImageZoom from 'react-inner-image-zoom';
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.min.css';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import SmallSpinner from 'modules/shared/SmallSpinner';
import { useDebounce, useImageOnLoad } from 'modules/shared/hooks';
import { SliderImage } from 'entities/Photo/interfaces';

import styles from './Carousel.module.scss';

type Props = {
  images: SliderImage[];
  index?: number;
  setIndex?: any;
  withControls?: boolean;
  openZoomHandler?: () => void;
  clearCanvas?: boolean;
};

const Carousel: React.FC<Props> = ({
  images,
  setIndex,
  index = 0,
  withControls = false,
  clearCanvas,
  openZoomHandler = () => null,
}) => {
  const slider = useRef<Slider>(null);
  const debIndex = useDebounce(index, 300);
  const loading = index !== debIndex;

  const handlePrevSlide = () => {
    setIndex((prev: number) => (prev ? prev - 1 : 0));
  };
  const handleNextSlide = () => {
    const imgSize = images.length;
    setIndex((prev: number) => (prev + 1 !== imgSize ? prev + 1 : prev));
  };

  const { handleImageOnLoad, css } = useImageOnLoad();

  useEffect(() => {
    // @ts-ignore
    slider?.current?.slickGoTo(debIndex);
  }, [debIndex, images]);

  const settings = {
    dots: false,
    arrows: false,
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 200,
    fade: true,
    swipe: false,
    easing: 'ease-in-out',
  };

  return (
    <>
      {/* @ts-ignore */}
      <Slider
        className={classNames(styles.carousel, {
          [styles.modalCarousel]: withControls,
        })}
        initialSlide={debIndex}
        ref={slider}
        {...settings}
      >
        {images.map((img, idx: number) => (
          <div key={idx} className={styles.slide} {...(!withControls && { onDoubleClick: openZoomHandler })}>
            {loading && <SmallSpinner className={withControls ? styles.loaderModal : styles.loaderSmall} />}
            {withControls ? (
              <div>
                {idx === debIndex ? (
                  <InnerImageZoom
                    src={img.picture}
                    zoomSrc={img.picture}
                    srcSet={img.picture}
                    alt="Фото недоступне"
                    width={600}
                    id={`preview-${idx}`}
                    hideHint
                  />
                ) : null}
              </div>
            ) : (
              <img
                className={styles.img}
                src={img.picture}
                id="preview"
                alt="Фото недоступне"
                onLoad={handleImageOnLoad}
                style={{ ...css.fullSize }}
              />
            )}
          </div>
        ))}
      </Slider>
      <div className={clearCanvas ? styles.numbersAbs : styles.numbers}>
        {!clearCanvas && withControls && (
          <h3 className="mt-2 text-center">
            {images[debIndex]?.type === 'vehicle' && 'Номер техпаспорту автомобіля: '}
            {images[debIndex]?.type === 'trailer' && 'Номер техпаспорту причепу: '}
            {images[debIndex]?.type === 'driver' && images[index]?.docType === 1 && 'Номер посвідчення водія: '}
            {images[debIndex]?.type === 'driver' && images[index]?.docType === 3 ? 'Номер паспорту водія: ' : null}
            {images[debIndex]?.type === 'forwarder' && 'Номер паспорту експедитора: '}
            {images[index]?.docNumber}
          </h3>
        )}
        {images.length > 1 && !clearCanvas ? (
          <div className="d-flex my-1 align-items-center">
            <Button
              className={classNames('border-0 p-1', styles.control, {
                [styles.controlDark]: withControls,
              })}
              variant="outline-light"
              onClick={handlePrevSlide}
            >
              <ArrowLeftIcon />
            </Button>
            <span className="px-1 text-dark">{`Фото ${index + 1} з ${images.length}`}</span>
            <Button
              className={classNames('border-0 p-1', styles.control, {
                [styles.controlDark]: withControls,
              })}
              variant="outline-light"
              onClick={handleNextSlide}
            >
              <ArrowRightIcon />
            </Button>
          </div>
        ) : null}
        {images.length === 1 && !clearCanvas ? <span className="my-2 py-1 text-dark">Фото 1 з 1</span> : null}
      </div>
    </>
  );
};

export default Carousel;
